import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as echarts from 'echarts';
import { useTheme } from '../../../../theme/ThemeContext';
import { useTimezone } from '@app/contexts/useTimezone';
import { ChartCardProps, ChartMode, TooltipOrderMode } from './types';
import { VARIABLE_CONFIG } from './config';
import { useChartDataFromContext } from './hooks/useChartDataFromContext';
import { useChartOptions } from './hooks/useChartOptions';
import { ChartHeader } from './ChartHeader';
import { MARKET_ZONES } from '@app/common/static-data/energy-markets';
import { useMarketAggregates } from '../../contexts/MarketAggregatesContext';
import { DataZoomRange } from './utils/dataZoomConfig';

const ChartCard: React.FC<ChartCardProps> = ({
  selectedVariable,
  selectedZone,
  tabId,
  cardId,
  cardName,
  onCardNameChange,
}) => {
  const { theme } = useTheme();
  const { timezone, formatInUserTimezone } = useTimezone();
  const isDark = theme === 'dark';
  const axisColor = isDark ? '#D1D5DB' : '#6B7280';
  const { state } = useMarketAggregates();

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chartMode, setChartMode] = useState<ChartMode>('data');
  const [selectedBaseline, setSelectedBaseline] = useState<string | null>(null);
  const [tooltipOrderMode, setTooltipOrderMode] = useState<TooltipOrderMode>('value');
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const lastZoomRangeRef = useRef<DataZoomRange | null>(null);

  // Get current variable configuration (default to wind if null)
  const currentConfig = VARIABLE_CONFIG[selectedVariable || 'wind'];

  // Create title with zone information
  const chartTitle = useMemo(() => {
    const baseTitle = cardName || 'Charts';

    if (!selectedZone) {
      return baseTitle;
    }

    if (Array.isArray(selectedZone)) {
      const labels = selectedZone
        .map((z) => MARKET_ZONES.find((zone) => zone.id === z)?.label || z)
        .join(', ');
      return `${baseTitle} - ${labels}`;
    }

    const zoneInfo = MARKET_ZONES.find((zone) => zone.id === selectedZone);
    const zoneName = zoneInfo ? zoneInfo.label : selectedZone;

    return `${baseTitle} - ${zoneName}`;
  }, [selectedZone, cardName]);

  // Handler for title changes
  const handleTitleChange = (newTitle: string) => {
    if (onCardNameChange && cardId) {
      onCardNameChange(cardId, newTitle);
    }
  };

  // Determine what's missing for a helpful message
  const getMissingRequirementsMessage = useMemo(() => {
    if (!selectedZone || (Array.isArray(selectedZone) && selectedZone.length === 0)) {
      return 'Select one or more market zones to view data';
    }

    const selectedModels = state.availableModels.filter((model) => model.isSelected);
    if (selectedModels.length === 0) {
      return 'Select models to view data';
    }

    const selectedRuns = selectedModels.filter((model) => model.selectedRuns.length > 0);
    if (selectedRuns.length === 0) {
      return 'Select model runs to view data';
    }

    return 'No data available';
  }, [selectedZone, state.availableModels]);

  // Use custom hooks
  const { chartData, loading, error, availableBaselines } = useChartDataFromContext(
    selectedVariable,
    selectedZone,
    chartMode,
    selectedBaseline,
    startDate,
    endDate,
  );

  // Auto-reset baseline selection when available baselines change
  useEffect(() => {
    if (
      availableBaselines.length > 0 &&
      (!selectedBaseline || !availableBaselines.includes(selectedBaseline))
    ) {
      setSelectedBaseline(availableBaselines[0]);
    }
  }, [availableBaselines, selectedBaseline]);

  // Persist and restore UI selections per tab
  useEffect(() => {
    if (!tabId) return;
    try {
      const raw = localStorage.getItem(`ma-chart-ui-${tabId}`);
      if (raw) {
        const parsed = JSON.parse(raw) as {
          chartMode?: ChartMode;
          selectedBaseline?: string | null;
          tooltipOrderMode?: TooltipOrderMode;
          zoomRange?: DataZoomRange;
        };
        if (parsed.chartMode) setChartMode(parsed.chartMode);
        if (parsed.tooltipOrderMode) setTooltipOrderMode(parsed.tooltipOrderMode);
        if (parsed.selectedBaseline) setSelectedBaseline(parsed.selectedBaseline);
        if (parsed.zoomRange) lastZoomRangeRef.current = parsed.zoomRange;
      }
    } catch {
      // Intentionally ignore localStorage/JSON errors
    }
  }, [tabId]);

  useEffect(() => {
    if (!tabId) return;
    const payload = {
      chartMode,
      selectedBaseline,
      tooltipOrderMode,
    };
    try {
      localStorage.setItem(`ma-chart-ui-${tabId}`, JSON.stringify(payload));
    } catch {
      // Intentionally ignore localStorage errors
    }
  }, [tabId, chartMode, selectedBaseline, tooltipOrderMode]);
  const chartOptions = useChartOptions({
    chartData,
    selectedVariable,
    currentConfig,
    isDark,
    axisColor,
    chartMode,
    tooltipOrderMode,
    timezone,
    formatInUserTimezone,
    initialZoomRange: lastZoomRangeRef.current ?? undefined,
  });

  // Initialize chart instance
  useEffect(() => {
    if (chartRef.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, {
        renderer: 'canvas',
      });
    }

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  // Set up resize observer to handle card resizing in grid layout
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(() => {
      // Small delay to ensure the container has updated
      setTimeout(() => {
        if (chartInstance.current) {
          chartInstance.current.resize();
        }
      }, 100);
    });

    resizeObserver.observe(containerRef.current);
    resizeObserverRef.current = resizeObserver;

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Update chart when options change
  useEffect(() => {
    if (!chartInstance.current || !Object.keys(chartOptions).length) {
      return;
    }

    chartInstance.current.clear();
    chartInstance.current.setOption(chartOptions);

    // Re-apply last saved zoom range if any (in case ECharts reset it)
    if (lastZoomRangeRef.current) {
      chartInstance.current.dispatchAction({
        type: 'dataZoom',
        start: lastZoomRangeRef.current.start,
        end: lastZoomRangeRef.current.end,
      });
    }
  }, [chartOptions]);

  // Handle dataZoom events to persist zoom
  useEffect(() => {
    if (!chartInstance.current) return;
    const handler = (params: any) => {
      // DataZoom event returns start/end percentages; capture them
      if (Array.isArray(params.batch) && params.batch.length > 0) {
        const b = params.batch[0];
        const range: DataZoomRange = { start: b.start, end: b.end };
        lastZoomRangeRef.current = range;
        if (tabId) {
          try {
            const raw = localStorage.getItem(`ma-chart-ui-${tabId}`);
            const base = raw ? JSON.parse(raw) : {};
            localStorage.setItem(
              `ma-chart-ui-${tabId}`,
              JSON.stringify({ ...base, zoomRange: range }),
            );
          } catch {
            // Intentionally ignore localStorage/JSON errors
          }
        }
      }
    };
    chartInstance.current.on('dataZoom', handler);
    return () => {
      chartInstance.current?.off('dataZoom', handler);
    };
  }, [tabId]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const downloadChart = () => {
    if (chartInstance.current) {
      const url = chartInstance.current.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
      });
      const link = document.createElement('a');

      // Include zone in filename if selected
      const zoneSuffix = Array.isArray(selectedZone)
        ? `-${selectedZone.join(',')}`
        : selectedZone
          ? `-${(
              MARKET_ZONES.find((zone) => zone.id === selectedZone)?.label || selectedZone
            ).replace(/\s+/g, '-')}`
          : '';
      const timestamp = new Date().toISOString().split('T')[0];

      link.download = `${currentConfig.filename}${zoneSuffix}-${timestamp}.png`;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div
      ref={containerRef}
      className="h-full bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
      data-card-container
    >
      <div className="h-full flex flex-col">
        <ChartHeader
          title={chartTitle}
          onTitleChange={onCardNameChange ? handleTitleChange : undefined}
          onToggleFullscreen={toggleFullscreen}
          onDownload={downloadChart}
          isFullscreen={isFullscreen}
          chartMode={chartMode}
          onToggleMode={setChartMode}
          availableBaselines={availableBaselines}
          selectedBaseline={selectedBaseline}
          onBaselineChange={setSelectedBaseline}
          tooltipOrderMode={tooltipOrderMode}
          onTooltipOrderModeChange={setTooltipOrderMode}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
        />

        {/* Chart Container */}
        <div className="flex-1 relative p-2">
          {/* Always render the chart div for ECharts */}
          <div
            ref={chartRef}
            className="absolute inset-2 w-auto h-auto"
            style={{ minHeight: '200px' }}
          />

          {/* Overlay messages */}
          {loading && (
            <div className="absolute inset-2 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90">
              <div className="text-gray-500 dark:text-gray-400">Loading chart data...</div>
            </div>
          )}

          {!loading && error && (
            <div className="absolute inset-2 flex flex-col items-center justify-center bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90">
              <div className="text-red-500 dark:text-red-400 text-center">
                <p className="font-medium">Failed to load chart data</p>
                <p className="text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && chartData.length === 0 && (
            <div className="absolute inset-2 flex items-center justify-center bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90">
              <div className="text-gray-500 dark:text-gray-400 text-center">
                <p>{getMissingRequirementsMessage}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChartCard;
